import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  maxCharactersValidator,
  requiredValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import { useState } from "react";
import { CalculationType } from "../../../../../../../../../../api/types/fieldConfigurationTypes";
import type { ReportField } from "../../../../../../../../../../api/types/svTypes";
import { getFieldCaption } from "../../../../../../utilities/Utilities";
import { isCustomColumn } from "../../../../../../utilities/fieldsHelper";

export interface FormDataFields {
  name: string;
  selectedFormulaParameterIds: string[];
  selectedCalculationType: CalculationType;
}

interface FormState extends FormDataFields {
  isValid?: boolean;
  nameResult?: ValidationResult;
  selectedFormulaParametersResult?: ValidationResult;
  selectedCalculationTypeResult?: ValidationResult;
  touchedFields: (keyof FormDataFields)[];
}

const getPredefinedFormulaParameterIds = (currentField: ReportField | undefined): string[] => {
  if (!currentField) {
    return [];
  }
  if (isCustomColumn(currentField)) {
    if (currentField.invalid) {
      return [];
    }
    return currentField.config.formulaParameters ?? [];
  }
  return [currentField.config.guid];
};

const getPredefinedFieldName = (
  initialCalculationType: CalculationType,
  currentField: ReportField | undefined,
  aggregationFields: ReportField[]
) => {
  if (isCustomColumn(currentField)) {
    return currentField.config.customLabel ?? currentField.config.name ?? "";
  }
  if (initialCalculationType === CalculationType.PercentageOfTotal) {
    const field = aggregationFields.find((field) => field.config.guid === currentField?.config.guid);
    if (field) {
      const caption = getFieldCaption(field);
      return `% of ${caption}`;
    }
  }
  return "";
};

const validateName = combineValidators(requiredValidator, maxCharactersValidator(255));

const validateForm = (form: FormState) => {
  const selectedFormulaParameterIdsResult = requiredValidator(form.selectedFormulaParameterIds?.[0] ?? "");
  const selectedCalculationTypeResult = validResult();
  const nameResult = validateName(form.name);
  const isValid =
    nameResult.isValid && selectedFormulaParameterIdsResult.isValid && selectedCalculationTypeResult.isValid;

  return {
    isValid,
    nameResult,
    selectedFormulaParameterIdsResult,
    selectedCalculationTypeResult,
  };
};

const useSetupCustomCalculationMenu = (
  initialCalculationType: CalculationType,
  fieldGuid: string | undefined,
  aggregationFields: ReportField[]
) => {
  const [form, setForm] = useState<FormState>(() => {
    const currentField = aggregationFields.find((field) => field.config.guid === fieldGuid);
    const initForm: FormState = {
      name: getPredefinedFieldName(initialCalculationType, currentField, aggregationFields),
      selectedFormulaParameterIds: getPredefinedFormulaParameterIds(currentField),
      selectedCalculationType: initialCalculationType,
      touchedFields: [],
    };
    const validationResult = validateForm(initForm);
    return { ...initForm, ...validationResult };
  });

  const handleNameChange = (name: string) => {
    setForm((prev: FormState) => {
      const newForm: FormState = {
        ...prev,
        name,
        touchedFields: distinct([...prev.touchedFields, "name"]),
      };
      const validationResult = validateForm(newForm);
      return { ...newForm, ...validationResult };
    });
  };

  const handleSelectedFormulaParameterIdsChange = (selectedFormulaParameterIds: string[]) => {
    setForm((prev: FormState) => {
      const newForm: FormState = {
        ...prev,
        selectedFormulaParameterIds,
        touchedFields: distinct([...prev.touchedFields, "selectedFormulaParameterIds"]),
      };
      const validationResult = validateForm(newForm);
      return { ...newForm, ...validationResult };
    });
  };

  const handleSelectedCalculationTypeChange = (selectedCalculationType: CalculationType) => {
    setForm((prev: FormState) => {
      const newForm: FormState = {
        ...prev,
        selectedCalculationType,
        touchedFields: distinct([...prev.touchedFields, "selectedCalculationType"]),
      };
      const validationResult = validateForm(newForm);
      return { ...newForm, ...validationResult };
    });
  };

  return {
    isValid: form.isValid,
    selectedCalculationType: form.selectedCalculationType,
    selectedFormulaParameterIds: form.selectedFormulaParameterIds,
    name: form.name,
    nameError: form.touchedFields.includes("name") ? form.nameResult?.error : undefined,
    selectedFormulaParameterIdsError: form.touchedFields.includes("selectedFormulaParameterIds")
      ? form.selectedFormulaParametersResult?.error
      : undefined,
    onNameChange: handleNameChange,
    onSelectedFormulaParameterIdsChange: handleSelectedFormulaParameterIdsChange,
    onSelectedCalculationTypeChange: handleSelectedCalculationTypeChange,
    isDirty: form.touchedFields.length > 0,
  };
};

export default useSetupCustomCalculationMenu;
